import { useCallback, useState } from "react";

/**
 * A piece of UI state remembered in this browser, the standard way across the
 * app to make a view "remember your last pick" — a chosen layout, a filter, a
 * sort. Reads synchronously on first render so the remembered value is what
 * paints, never a default that flips a frame later (the flicker `use-rail.ts`
 * documents avoiding). Namespaced under `astrum.*` like every other stored
 * preference.
 *
 * Per browser, not per account: a layout preference is about this screen on
 * this device, not about the person — someone on a wide desktop and a small
 * laptop can want different answers, and a server round-trip would flicker the
 * wrong choice in on every load. For anything that should follow the person
 * between devices, store it on the account instead.
 *
 * `key` must be stable and unique; `fallback` is used when nothing is stored,
 * or when storage is unavailable (private browsing, blocked site data — both
 * throw, and are handled as "no stored value").
 */
export function usePersistentState<T extends string>(
  key: string,
  fallback: T,
  allowed?: readonly T[],
): [T, (next: T) => void] {
  const storageKey = `astrum.${key}`;

  const [value, setValue] = useState<T>(() => {
    try {
      const stored = window.localStorage.getItem(storageKey);
      if (stored === null) return fallback;
      // A stored value that is no longer valid (an option renamed or removed
      // since it was saved) falls back rather than rendering a broken state.
      if (allowed && !allowed.includes(stored as T)) return fallback;
      return stored as T;
    } catch {
      return fallback;
    }
  });

  const set = useCallback(
    (next: T) => {
      setValue(next);
      try {
        window.localStorage.setItem(storageKey, next);
      } catch {
        // The choice still applies this session; it just will not be
        // remembered. Not worth surfacing to the user.
      }
    },
    [storageKey],
  );

  return [value, set];
}
